import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ModelRecord } from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import { useDeleteModel, useDelistModel, useListModel, useModelsList } from '../api/useModels.ts';
import { Modal } from '../components/Modal.tsx';
import { useToast, coerceErrorMessage } from '../components/Toast.tsx';
import { useT } from '../i18n/index.ts';

/**
 * `/models` list view. DESIGN.md §6 Data tables: no horizontal dividers,
 * zebra via `surface-container` vs `surface-container-low`, dense row
 * padding, `label-md` uppercase column headers.
 *
 * Operator affordances:
 *   - Client-side search across vendor / model id / display name.
 *   - Vendor filter sits above the table so the row itself can stay compact.
 *   - Vendor discovery creates delisted candidates; this page lists and
 *     shelves those candidates instead of registering model ids by hand.
 *   - Per-row Manage (navigates to detail) + Delete (soft-delete confirm).
 */

const PRICE_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 6,
});

function formatUsdPerMtok(value: number): string {
  return PRICE_FORMATTER.format(value);
}

function hasCachePricing(row: ModelRecord): boolean {
  const pricing = row.current_pricing;
  return (
    pricing !== null &&
    pricing !== undefined &&
    (pricing.cache_read_per_mtok_usd > 0 || pricing.cache_write_per_mtok_usd > 0)
  );
}

interface RowProps {
  readonly row: ModelRecord;
  readonly zebra: 'odd' | 'even';
  readonly onDelete: (row: ModelRecord) => void;
  readonly onShelfToggle: (row: ModelRecord) => void;
  readonly deleting: boolean;
  readonly shelfing: boolean;
  readonly manageLabel: string;
  readonly deleteLabel: string;
  readonly listLabel: string;
  readonly delistLabel: string;
}

function ModelRow({
  row,
  zebra,
  onDelete,
  onShelfToggle,
  deleting,
  shelfing,
  manageLabel,
  deleteLabel,
  listLabel,
  delistLabel,
}: RowProps): JSX.Element {
  const t = useT();
  const zebraClass = zebra === 'odd' ? 'bg-surface-container' : 'bg-surface-container-low';
  const idAttr = `${row.vendor}-${row.model}`;
  const detailPath = `/supply/models/${encodeURIComponent(row.vendor)}/${encodeURIComponent(row.model)}`;
  return (
    <tr
      data-testid={`model-row-${idAttr}`}
      className={`${zebraClass} transition-colors hover:bg-surface-bright`}
    >
      <td className="px-4 py-3 text-[13px] font-semibold text-on-surface">
        <Link
          to={detailPath}
          className="hover:text-primary focus-visible:outline-none focus-visible:underline"
        >
          {row.model}
        </Link>
      </td>
      <td className="px-4 py-3 text-[13px] text-on-surface">{row.display_name ?? '-'}</td>
      <td className="px-4 py-3 text-[12px] text-on-surface">
        {row.current_pricing !== null && row.current_pricing !== undefined ? (
          <div className="flex min-w-[128px] flex-col gap-1">
            <span className="font-mono text-[13px] font-semibold text-on-surface">
              {formatUsdPerMtok(row.current_pricing.input_per_mtok_usd)}
              {' / '}
              {formatUsdPerMtok(row.current_pricing.output_per_mtok_usd)}
            </span>
            <span className="text-[11px] text-outline">
              {hasCachePricing(row)
                ? t('models.price.cache_line', {
                    read: formatUsdPerMtok(row.current_pricing.cache_read_per_mtok_usd),
                    write: formatUsdPerMtok(row.current_pricing.cache_write_per_mtok_usd),
                  })
                : t('models.price.unit')}
            </span>
          </div>
        ) : (
          <span className="inline-flex items-center rounded-md bg-surface-container-high px-2 py-1 text-[11px] font-semibold text-outline">
            {t('models.price.unpriced')}
          </span>
        )}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => onShelfToggle(row)}
            disabled={shelfing}
            data-testid={`model-row-${idAttr}-shelf`}
            className="inline-flex items-center justify-center rounded-lg px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-primary transition-colors hover:bg-surface-container-highest focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container disabled:opacity-40 disabled:pointer-events-none"
          >
            {row.shelf_status === 'listed' ? delistLabel : listLabel}
          </button>
          <Link
            to={detailPath}
            data-testid={`model-row-${idAttr}-manage`}
            className="inline-flex items-center justify-center rounded-lg px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-primary transition-colors hover:bg-surface-container-highest focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container"
          >
            {manageLabel}
          </Link>
          <button
            type="button"
            onClick={() => onDelete(row)}
            disabled={deleting}
            data-testid={`model-row-${idAttr}-delete`}
            className="inline-flex items-center justify-center rounded-lg px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-on-error-container transition-colors hover:bg-error-container focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container disabled:opacity-40 disabled:pointer-events-none"
          >
            {deleteLabel}
          </button>
        </div>
      </td>
    </tr>
  );
}

interface DeleteTarget {
  readonly vendor: string;
  readonly model: string;
}

export function ModelsList(): JSX.Element {
  const t = useT();
  const list = useModelsList();
  const deleteMutation = useDeleteModel();
  const listMutation = useListModel();
  const delistMutation = useDelistModel();
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [vendorFilter, setVendorFilter] = useState('all');
  const [shelfTab, setShelfTab] = useState<'listed' | 'delisted'>('listed');
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const vendorOptions = useMemo(() => {
    return Array.from(new Set((list.data ?? []).map((row) => row.vendor))).sort((a, b) =>
      a.localeCompare(b),
    );
  }, [list.data]);

  useEffect(() => {
    if (vendorFilter !== 'all' && !vendorOptions.includes(vendorFilter)) {
      setVendorFilter('all');
    }
  }, [vendorFilter, vendorOptions]);

  const vendorFilteredRows = useMemo(() => {
    const rows = list.data ?? [];
    if (vendorFilter === 'all') {
      return rows;
    }
    return rows.filter((row) => row.vendor === vendorFilter);
  }, [list.data, vendorFilter]);

  const shelfCounts = useMemo(() => {
    return {
      listed: vendorFilteredRows.filter((row) => row.shelf_status === 'listed').length,
      delisted: vendorFilteredRows.filter((row) => row.shelf_status === 'delisted').length,
    };
  }, [vendorFilteredRows]);

  const filtered = useMemo(() => {
    const rows = vendorFilteredRows.filter((row) => row.shelf_status === shelfTab);
    const query = search.trim().toLowerCase();
    if (query.length === 0) {
      return rows;
    }
    return rows.filter(
      (row) =>
        row.vendor.toLowerCase().includes(query) ||
        row.model.toLowerCase().includes(query) ||
        (row.display_name ?? '').toLowerCase().includes(query),
    );
  }, [search, shelfTab, vendorFilteredRows]);

  async function confirmDelete(): Promise<void> {
    if (deleteTarget === null) {
      return;
    }
    setDeleteError(null);
    try {
      await deleteMutation.mutateAsync({
        vendor: deleteTarget.vendor,
        model: deleteTarget.model,
      });
      setDeleteTarget(null);
    } catch (err) {
      const message =
        err instanceof OpsApiError ? `${err.code}: ${err.message}` : coerceErrorMessage(err);
      setDeleteError(message);
      toast.error(message);
    }
  }

  async function toggleShelf(row: ModelRecord): Promise<void> {
    try {
      if (row.shelf_status === 'listed') {
        await delistMutation.mutateAsync({
          vendor: row.vendor,
          model: row.model,
          reason: 'ops manual delist',
        });
        toast.success('模型已下架');
      } else {
        await listMutation.mutateAsync({
          vendor: row.vendor,
          model: row.model,
          reason: 'ops manual list',
        });
        toast.success('模型已上架');
      }
    } catch (err) {
      toast.error(coerceErrorMessage(err));
    }
  }

  return (
    <section data-testid="models-list-page" className="flex flex-col gap-8">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex flex-col gap-2">
          <h2 className="text-3xl font-semibold tracking-tight text-on-surface">
            {t('models.title')}
          </h2>
          <p className="text-sm text-outline">{t('models.subtitle')}</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex h-10 items-center gap-2 rounded-lg bg-surface-container-lowest px-3 focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2 focus-within:ring-offset-surface">
            <span aria-hidden="true" className="material-symbols-outlined text-[16px] text-outline">
              search
            </span>
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              placeholder={t('models.search.placeholder')}
              aria-label={t('models.search.aria_label')}
              data-testid="models-search"
              className="w-72 bg-transparent text-[13px] text-on-surface placeholder:text-outline focus:outline-none"
            />
          </label>
          <Link
            to="/supply/vendors"
            data-testid="models-discover"
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-primary transition-all hover:shadow-[0_0_16px_rgba(173,198,255,0.2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface"
          >
            <span aria-hidden="true" className="material-symbols-outlined text-[16px]">
              travel_explore
            </span>
            {t('models.new')}
          </Link>
        </div>
      </header>

      {list.isError ? (
        <div
          role="alert"
          data-testid="models-error"
          className="flex items-center justify-between gap-4 rounded-xl bg-error-container px-6 py-4 text-on-error-container"
        >
          <div className="flex flex-col gap-1">
            <span className="text-sm font-semibold">{t('models.error.title')}</span>
            <span className="text-xs">
              {list.error instanceof OpsApiError
                ? `${list.error.code}: ${list.error.message}`
                : t('common.network_error')}
            </span>
          </div>
          <button
            type="button"
            onClick={() => void list.refetch()}
            data-testid="models-retry"
            className="inline-flex items-center justify-center rounded-lg bg-surface-container-highest px-4 py-2 text-[11px] font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-error-container"
          >
            {t('common.retry')}
          </button>
        </div>
      ) : null}

      {list.isSuccess ? (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div
            data-testid="models-shelf-tabs"
            className="inline-flex w-fit rounded-lg bg-surface-container-low p-1"
            role="tablist"
            aria-label="模型上下架状态"
          >
            {(['listed', 'delisted'] as const).map((status) => {
              const active = shelfTab === status;
              const label = status === 'listed' ? '已上架' : '已下架';
              const count = shelfCounts[status];
              return (
                <button
                  key={status}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  data-testid={`models-tab-${status}`}
                  onClick={() => setShelfTab(status)}
                  className={
                    active
                      ? 'inline-flex items-center gap-2 rounded-md bg-surface-container-highest px-4 py-2 text-xs font-semibold text-on-surface shadow-sm'
                      : 'inline-flex items-center gap-2 rounded-md px-4 py-2 text-xs font-semibold text-on-surface-variant transition-colors hover:bg-surface-container'
                  }
                >
                  <span>{label}</span>
                  <span className="font-mono text-[11px]">{count}</span>
                </button>
              );
            })}
          </div>
          <label className="flex w-full max-w-xs items-center gap-2 rounded-lg bg-surface-container-low px-3 py-2 text-xs font-semibold text-on-surface-variant focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2 focus-within:ring-offset-surface sm:w-auto">
            <span>{t('models.filter.vendor')}</span>
            <select
              value={vendorFilter}
              onChange={(e) => setVendorFilter(e.currentTarget.value)}
              data-testid="models-vendor-filter"
              className="min-w-36 flex-1 bg-transparent font-mono text-[12px] text-on-surface focus:outline-none"
            >
              <option value="all">{t('models.filter.vendor_all')}</option>
              {vendorOptions.map((vendor) => (
                <option key={vendor} value={vendor}>
                  {vendor}
                </option>
              ))}
            </select>
          </label>
        </div>
      ) : null}

      {list.isPending ? (
        <div
          data-testid="models-loading"
          className="flex flex-col gap-2 rounded-xl bg-surface-container p-4"
        >
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-12 animate-pulse rounded-lg bg-surface-container-low"
              aria-hidden="true"
            />
          ))}
          <span className="sr-only">{t('models.loading_aria')}</span>
        </div>
      ) : null}

      {list.isSuccess && filtered.length === 0 ? (
        <div
          data-testid="models-empty"
          className="flex flex-col items-center gap-4 rounded-xl bg-surface-container px-8 py-16 text-center"
        >
          <h3 className="text-2xl font-semibold tracking-tight text-on-surface">
            {search.length > 0 ? t('models.empty.search.title') : t('models.empty.initial.title')}
          </h3>
          <p className="max-w-md text-sm text-outline">
            {search.length > 0 ? t('models.empty.search.body') : t('models.empty.initial.body')}
          </p>
        </div>
      ) : null}

      {list.isSuccess && filtered.length > 0 ? (
        <div className="overflow-hidden rounded-xl bg-surface-container">
          <table data-testid="models-table" className="w-full border-separate border-spacing-0">
            <thead>
              <tr className="bg-surface-container-high">
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-[0.1em] text-on-surface-variant"
                >
                  {t('models.column.model')}
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-[0.1em] text-on-surface-variant"
                >
                  {t('models.column.display_name')}
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-[0.1em] text-on-surface-variant"
                >
                  {t('models.column.price')}
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-right text-[10px] font-bold uppercase tracking-[0.1em] text-on-surface-variant"
                >
                  {t('models.column.actions')}
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row, i) => (
                <ModelRow
                  key={`${row.vendor}:${row.model}`}
                  row={row}
                  zebra={i % 2 === 0 ? 'odd' : 'even'}
                  onShelfToggle={(target) => void toggleShelf(target)}
                  onDelete={(target) => {
                    setDeleteError(null);
                    setDeleteTarget({ vendor: target.vendor, model: target.model });
                  }}
                  deleting={
                    deleteMutation.isPending &&
                    deleteMutation.variables?.vendor === row.vendor &&
                    deleteMutation.variables?.model === row.model
                  }
                  shelfing={
                    (listMutation.isPending || delistMutation.isPending) &&
                    ((listMutation.variables?.vendor === row.vendor &&
                      listMutation.variables?.model === row.model) ||
                      (delistMutation.variables?.vendor === row.vendor &&
                        delistMutation.variables?.model === row.model))
                  }
                  manageLabel={t('models.row.manage')}
                  deleteLabel={t('models.row.delete')}
                  listLabel="上架"
                  delistLabel="下架"
                />
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {deleteTarget !== null ? (
        <DeleteModelModal
          target={deleteTarget}
          busy={deleteMutation.isPending}
          error={deleteError}
          onCancel={() => {
            if (!deleteMutation.isPending) {
              setDeleteTarget(null);
              setDeleteError(null);
            }
          }}
          onConfirm={() => void confirmDelete()}
        />
      ) : null}
    </section>
  );
}

interface DeleteModalProps {
  readonly target: DeleteTarget;
  readonly busy: boolean;
  readonly error: string | null;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
}

function DeleteModelModal({
  target,
  busy,
  error,
  onCancel,
  onConfirm,
}: DeleteModalProps): JSX.Element {
  const t = useT();
  return (
    <Modal isOpen onClose={onCancel} titleId="delete-model-title" testId="model-delete-confirm">
      <div className="flex flex-col gap-6 p-8">
        <h2 id="delete-model-title" className="text-base font-semibold text-on-surface">
          {t('models.delete_modal.title')}
        </h2>
        <p className="text-sm text-outline">
          <span className="font-mono font-semibold text-on-surface">
            {target.vendor}/{target.model}
          </span>
          {t('models.delete_modal.body_suffix')}
          <code className="font-mono">{t('models.delete_modal.body_deleted_at_code')}</code>
          {t('models.delete_modal.body_tail')}
        </p>
        {error !== null ? (
          <p
            className="text-xs font-medium text-error"
            role="alert"
            data-testid="model-delete-error"
          >
            {error}
          </p>
        ) : null}
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="inline-flex items-center justify-center rounded-lg bg-surface-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container-highest disabled:opacity-40 disabled:pointer-events-none"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            data-testid="model-delete-submit"
            className="inline-flex items-center justify-center rounded-lg bg-error-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-error-container transition-all hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container-highest disabled:opacity-40 disabled:pointer-events-none"
          >
            {busy ? t('common.deleting') : t('common.delete')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
